import {useState, useEffect, useCallback, useRef} from 'react'

// Configuration for the blacklist API
const BLACKLIST_API_BASE_URL = process.env.REACT_APP_BLACKLIST_API_URL || 'https://plebscan.org'
const BLACKLIST_AUTH_KEY = process.env.REACT_APP_BLACKLIST_AUTH_KEY // Optional auth key
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes cache
const RETRY_DELAY = 30 * 1000 // 30 seconds retry delay

// In-memory cache for blacklist data
let blacklistCache = {
  subplebbits: new Set(),
  authors: new Set(),
  comments: new Set(),
  lastUpdated: 0,
  isLoading: false,
}

/**
 * Hook for managing blacklist data from plebbit-indexer
 * Fetches and caches blacklisted subplebbits, authors, and comments
 */
const useBlacklist = () => {
  const [blacklist, setBlacklist] = useState({
    subplebbits: blacklistCache.subplebbits,
    authors: blacklistCache.authors,
    comments: blacklistCache.comments,
    isLoading: blacklistCache.isLoading,
    lastUpdated: blacklistCache.lastUpdated,
    error: null,
  })

  const retryTimeoutRef = useRef(null)

  /**
   * Fetches flagged posts from the API and updates the blacklist cache
   */
  const fetchBlacklistData = useCallback(async () => {
    // Check if cache is still valid
    const now = Date.now()
    if (now - blacklistCache.lastUpdated < CACHE_DURATION && blacklistCache.lastUpdated > 0) {
      console.log('[Blacklist] Using cached blacklist data')
      return
    }

    // Prevent multiple simultaneous requests
    if (blacklistCache.isLoading) {
      console.log('[Blacklist] Fetch already in progress')
      return
    }

    console.log('[Blacklist] Fetching blacklist data from API')
    blacklistCache.isLoading = true

    setBlacklist((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }))

    try {
      // Build API URL with auth if available
      let apiUrl = `${BLACKLIST_API_BASE_URL}/api/flagged-posts?status=moderated&limit=0`
      if (BLACKLIST_AUTH_KEY) {
        apiUrl += `&auth=${BLACKLIST_AUTH_KEY}`
      }

      const response = await fetch(apiUrl)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // Process the flagged posts and extract blacklisted items
      const newSubplebbits = new Set()
      const newAuthors = new Set()
      const newComments = new Set()

      if (data.flagged_posts && Array.isArray(data.flagged_posts)) {
        data.flagged_posts.forEach((post) => {
          switch (post.moderation_action) {
            case 'deindex_subplebbit':
              if (post.subplebbitAddress) {
                newSubplebbits.add(post.subplebbitAddress)
              }
              break
            case 'deindex_author':
              if (post.authorAddress) {
                newAuthors.add(post.authorAddress)
              }
              break
            case 'deindex_comment':
              if (post.id) {
                newComments.add(post.id)
              }
              break
          }
        })
      }

      // Update cache
      blacklistCache.subplebbits = newSubplebbits
      blacklistCache.authors = newAuthors
      blacklistCache.comments = newComments
      blacklistCache.lastUpdated = now
      blacklistCache.isLoading = false

      console.log('[Blacklist] Blacklist data updated:', {
        subplebbits: newSubplebbits.size,
        authors: newAuthors.size,
        comments: newComments.size,
      })

      setBlacklist({
        subplebbits: newSubplebbits,
        authors: newAuthors,
        comments: newComments,
        isLoading: false,
        lastUpdated: now,
        error: null,
      })
    } catch (error) {
      console.error('[Blacklist] Error fetching blacklist data:', error)

      blacklistCache.isLoading = false

      setBlacklist((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }))

      // Schedule retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }

      retryTimeoutRef.current = setTimeout(() => {
        console.log('[Blacklist] Retrying blacklist fetch after error')
        fetchBlacklistData()
      }, RETRY_DELAY)
    }
  }, [])

  /**
   * Manual refresh function
   */
  const refreshBlacklist = useCallback(() => {
    blacklistCache.lastUpdated = 0 // Force refresh
    fetchBlacklistData()
  }, [fetchBlacklistData])

  /**
   * Check if a subplebbit is blacklisted
   */
  const isSubplebbitBlacklisted = useCallback((subplebbitAddress) => {
    return blacklistCache.subplebbits.has(subplebbitAddress)
  }, [])

  /**
   * Check if an author is blacklisted
   */
  const isAuthorBlacklisted = useCallback((authorAddress) => {
    return blacklistCache.authors.has(authorAddress)
  }, [])

  /**
   * Check if a comment/post is blacklisted
   */
  const isCommentBlacklisted = useCallback((commentId) => {
    return blacklistCache.comments.has(commentId)
  }, [])

  /**
   * Check if a post should be filtered out based on blacklist rules
   */
  const isPostBlacklisted = useCallback(
    (post) => {
      if (!post) return false

      // Check if the specific comment is blacklisted
      if (post.cid && isCommentBlacklisted(post.cid)) {
        return true
      }

      // Check if the author is blacklisted
      if (post.author?.address && isAuthorBlacklisted(post.author.address)) {
        return true
      }

      // Check if the subplebbit is blacklisted
      if (post.subplebbitAddress && isSubplebbitBlacklisted(post.subplebbitAddress)) {
        return true
      }

      return false
    },
    [isCommentBlacklisted, isAuthorBlacklisted, isSubplebbitBlacklisted]
  )

  // Effect to fetch data on mount and set up periodic refresh
  useEffect(() => {
    fetchBlacklistData()

    // Set up periodic refresh
    const refreshInterval = setInterval(fetchBlacklistData, CACHE_DURATION)

    // Cleanup on unmount
    return () => {
      clearInterval(refreshInterval)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [fetchBlacklistData])

  return {
    blacklist,
    isSubplebbitBlacklisted,
    isAuthorBlacklisted,
    isCommentBlacklisted,
    isPostBlacklisted,
    refreshBlacklist,
  }
}

export default useBlacklist
